
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useSelector } from "react-redux";
import { NotificationProvider } from "./contexts/NotificationContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import ScrollToTop from "./components/common/ScrollToTop";
import EsignFontScope from "./components/common/EsignFontScope";

// Components
import Dashboard from "./features/dashboard/pages/DashboardPage";
import Login from "./components/auth/Login";
import AccountSetup from "./components/auth/AccountSetup";
import AssistantInvite from "./components/auth/AssistantInvite";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import ComingSoonRoute from "./components/auth/ComingSoonRoute";
import SuperAdminRoute from "./components/auth/SuperAdminRoute";
import MainLayout from "./components/layout/MainLayout";
import AiCallCenter from "./components/ai-dialer/AiCallCenter";
import CampaignSetupWizard from "./components/ai-dialer/CampaignSetupWizard";
import VoiceAgent from "./components/ai-dialer/VoiceAgent";
import PropertyListing from "./features/properties/pages/PropertyListingPage";
import PropertyTrashPage from "./features/properties/pages/PropertyTrashPage";
// "Public Properties" - the Database's own property layer, one level below
// appraisals and listings (Christian Gravias, CRM-2026-00031). Distinct from
// PublicPropertiesPage below, which is the external Public Property Search.
import DatabasePropertiesPage from "./features/properties/pages/DatabasePropertiesPage";
import DatabasePropertyProfilePage from "./features/properties/pages/DatabasePropertyProfilePage";
import AddProperty from "./features/properties/pages/AddPropertyPage";
import ViewProperty from "./components/properties/ViewProperty";
// Remove the chatbot until the LLM is perfected
// import ChatBot from "./components/chat/ChatBot";
import AgentManagement from "./components/agents/AgentManagement";
import BuyerManagement from "./components/buyers/BuyerManagement";
import LeadManagement from "./features/leads/pages/LeadsPage";
import ChatHistory from "./components/chat/ChatHistory";
import Settings from "./components/settings/Settings";
import MyProfile from "./components/settings/MyProfile";
import Calendar from "./components/calendar/Calendar";
import ForgotPassword from "./components/auth/ForgotPassword";
import AccountVerification from "./components/auth/AccountVerification";
import ResetPassword from "./components/auth/ResetPassword";
import TwilioAccountsManagement from "./components/settings/TwilioAccountsManagement";
import AdminProperties from "./features/properties/pages/PropertiesPage";
import CallLogsForQueue from "./components/properties/CallLogsForQueue";
import PropertyDetail from "./features/properties/pages/PropertyProfilePage";
import Notifications from "./components/notifications/Notifications";
import TasksManagement from "./components/tasks/TasksManagement";
import PipelinePage from "./components/pipeline";
import KPIsPage from "./features/kpi/pages/KPIPage";
import PropertyDetailPage from "./components/properties/PropertyDetailPage";
import CollingsPropertiesPage from "./features/properties/pages/CollingsPropertiesPage";
import CollingsPropertiesMapPage from "./features/properties/pages/CollingsPropertiesMapPage";
import CollingsPropertyDetailPage from "./features/properties/pages/CollingsPropertyDetailPage";
import AddCollingsPropertyPage from "./features/properties/pages/AddCollingsPropertyPage";
import PublicPropertiesPage from "./features/properties/pages/PublicPropertiesPage";
import PublicPropertyDetailPage from "./features/properties/pages/PublicPropertyDetailPage";
import AddPublicPropertyPage from "./features/properties/pages/AddPublicPropertyPage";
import CollingsDataPropertiesPage from "./features/properties/pages/CollingsDataPropertiesPage";
import PublicPropertyListingPage from "./features/properties/pages/PublicPropertyListingPage";
import OwnerRecordPage from "./features/properties/pages/OwnerRecordPage";
import PublicPropertyProfilePage from "./features/properties/pages/PublicPropertyProfilePage";
import PropertyProfileRedirect from "./features/properties/pages/PropertyProfileRedirect";
import AppraisalSubmissionsPage from "./features/properties/pages/AppraisalSubmissionsPage";
import CollingsDataPropertyDetailPage from "./features/properties/pages/CollingsDataPropertyDetailPage";
import PropertyDocumentsPage from "./components/properties/ViewDocuments";
// eSign (DocuSign-style signing) pages
import EsignHomePage from "./features/esign/pages/EsignHomePage";
import EsignAgreementsPage from "./features/esign/pages/EsignAgreementsPage";
import EnvelopeEditorPage from "./features/esign/pages/EnvelopeEditorPage";
import AuthorityFormPage from "./features/esign/pages/AuthorityFormPage";
import ContractOfSaleFormPage from "./features/esign/pages/ContractOfSaleFormPage";
import EnvelopeDetailPage from "./features/esign/pages/EnvelopeDetailPage";
import EsignSigningPage from "./features/esign/pages/EsignSigningPage";
import SmsOptOutPage from "./features/optout/pages/SmsOptOutPage";
import ShortLinkPage from "./features/optout/pages/ShortLinkPage";
import EmailUnsubscribePage from "./features/optout/pages/EmailUnsubscribePage";
import CannedResponsePage from "./components/settings/cannedResponse";
import REAProperties from "./components/rea/REAProperties";
import REAPropertyDetail from "./components/rea/REAPropertyDetail";
import DuplicateCheck from "./components/properties/DuplicateCheck";
// import EnquiryLeadQueues from "./components/enquiry-lead-queues/EnquiryLeadQueues";
import PropertyViewListing from "./components/lisiting-view/PropertyViewListing";
import PropertyViewListingDetail from "./components/lisiting-view/PropertyViewListingDetail";
import PropertyVoiceAgent from "./components/lisiting-view/PropertyVoiceAgent";
import ContactAgentForm from "./components/properties/ContactAgentForm";
// Remove the chatbot until the LLM is perfected
// import PropertyChatbot from "./components/chatbot/PropertyChatbot";
import Session from "./components/session/SessionMain";
import SessionDetails from "./components/session/SessionDetails";
import SessionAnalytics from "./components/session/SessionAnalytics";

// ✅ Your Google Calendar pages (note the folder name “google-calender”)
import GoogleCalender from "./components/google-calender/GoogleCalender";
import GoogleCalenderCallback from "./components/google-calender/GoogleCalenderCallback";
import AgentEnquiryLeads from "./components/agent-enquiry-leads/AgentEnquiryLeads";
import PipelinePageV2 from "./features/leads/pages/OffMarketPipelinePage";
import SellerLeadsPage from "./features/leads/pages/SellerLeadsPage";
import DatabasePage from "./features/database/pages/DatabasePage";
// Database + Seller Leads + Off/On Market Pipeline as one page (Monday 12988442847).
import ProspectsPage from "./features/prospects/pages/ProspectsPage";
import GarageSalesPage from "./features/garage-sales/pages/GarageSalesPage";
import ContactList from "./features/contacts/pages/ContactsPage";
import PortalInvitesPage from "./features/contacts/pages/PortalInvitesPage";
import SendRunPage from "./features/presend/pages/SendRunPage";
import ContactFormComponent from "./features/contacts/pages/ContactFormPage";
import ContactProfilePage from "./features/contacts/pages/ContactProfilePage";
import MailtoComposerScope from "./features/mailbox/components/MailtoComposerScope";
import ContactTrashPage from "./features/contacts/pages/ContactTrashPage";
import CampaignManager from "./components/campaign/CampaignManager";
import CommissionTracker from "./features/commissions/pages/CommissionsPage";
import LeaderboardStatPage from "./features/commissions/pages/LeaderboardStatPage";
import OFICheckIn from "./components/inspection/OFICheckIn";
import InspectionsPage from "./features/inspections/pages/InspectionsPage";
import OpenHomeSessionPage from "./features/inspections/pages/OpenHomeSessionPage";
import AutoEmailResponses from "./pages/AutoEmailResponses";
import DocumentsPage from "./pages/DocumentsPage";
import AnalyticsDashboardWrapper from "./features/analytics/pages/AnalyticsDashboardWrapper";
import OffersPage from "./features/offers/pages/OffersPage";
import OfferDetailPage from "./features/offers/pages/OfferDetailPage";
import SupportTicketsPage from "./features/support-tickets/pages/SupportTicketsPage";
import PropertyIdReviewPage from "./features/property-id-review/pages/PropertyIdReviewPage";
import AiSeoPage from "./features/ai-seo/pages/AiSeoPage";
import ReportIssuePage from "./features/error-reports/pages/ReportIssuePage";
import MailboxPage from "./features/mailbox/pages/MailboxPage";
import MessengerPage from "./features/messenger/pages/MessengerPage";
import PropertyAnalyticsDetailPage from "./features/analytics/pages/PropertyAnalyticsDetailPage";
import AgentDashboardPage from "./features/analytics/pages/AgentDashboardPage";
import CommunicationsPage from "./features/communications/pages/CommunicationsPage";
import CampaignEditorPage from "./features/communications/pages/CampaignEditorPage";
// Prospecting tab — the section between Properties and Pipeline.
import ProspectingLayout from "./features/prospecting/ProspectingLayout";
import RelistingOpportunitiesPage from "./features/prospecting/pages/RelistingOpportunitiesPage";
import NewDevelopmentsPage from "./features/prospecting/pages/NewDevelopmentsPage";
import ProspectingFindBuyersPage from "./features/prospecting/pages/ProspectingFindBuyersPage";
import MarketUpdatePage from "./features/prospecting/pages/MarketUpdatePage";
import MarketUpdateTemplatesPage from "./features/prospecting/pages/MarketUpdateTemplatesPage";
import MarketUpdateWizardPage from "./features/prospecting/pages/MarketUpdateWizardPage";
import CmaListPage from "./features/prospecting/pages/CmaListPage";
import CmaWizardPage from "./features/prospecting/pages/CmaWizardPage";
import CmaPublicReportPage from "./features/prospecting/pages/CmaPublicReportPage";
import MarketUpdatePublicPage from "./features/prospecting/pages/MarketUpdatePublicPage";
import ReportsPage from "./features/reports/pages/ReportsPage";
import { ensureCsrfToken } from "./api";
import { useEffect } from "react";

/** For properties-only PA: show Properties instead of Dashboard on login. */
function DashboardOrRedirect() {
  const { user } = useSelector((state) => state.auth);
  const isPropertiesOnlyPA = user?.role === "pa" && user?.pa_access_level === "properties_only";
  if (isPropertiesOnlyPA) return <Navigate to="/properties" replace />;
  return <Dashboard />;
}

/**
 * react-hot-toast styles its toasts inline (white card, dark text), which is
 * unreadable once the CRM is in dark mode, so the palette is passed in per theme.
 */
function ThemedToaster() {
  const { isDark } = useTheme();
  return (
    <Toaster
      // Top-center, not top-right: a top-right toast lands over the close "X" of
      // right-side drawers/modals (e.g. the Database contact drawer), so you had
      // to wait for it to fade before closing (CRM-2026-00067).
      position="top-center"
      toastOptions={
        isDark
          ? {
              style: {
                background: "#222933",
                color: "#e6edf3",
                border: "1px solid #3c4551",
              },
            }
          : undefined
      }
    />
  );
}

function App() {
  useEffect(() => {
    // Proactively fetch CSRF token on startup
    ensureCsrfToken();
  }, []);
  return (
    <NotificationProvider>
      <Router>
        <ThemeProvider>
        <ScrollToTop />
        {/* eSign keeps the old Urbanist font; everything else is Inter. */}
        <EsignFontScope />
        <Routes>
          {/* ***** Public routes (no layout) ***** */}
          {/* OAuth callback should be public so Google can redirect here reliably */}
          <Route path="/google-calender/callback" element={<GoogleCalenderCallback />} />

          {/* listing view components (public in your current setup) */}
          <Route path="/property-view-listing" element={<PropertyViewListing />} />
          <Route path="/property-view-listing/:id" element={<PropertyViewListingDetail />} />
          <Route path="/property-voice-agent" element={<PropertyVoiceAgent />} />
          <Route path="/contact-agent" element={<ContactAgentForm />} />
          {/* Remove the chatbot until the LLM is perfected */}
          {/* <Route path="/chatbot" element={<PropertyChatbot />} /> */}

          {/* OFI Check-in (public) */}
          <Route path="/ofi-checkin/:inspectionId" element={<OFICheckIn />} />

          {/* eSign public signing page — token is the credential, no auth/layout */}
          <Route path="/esign/sign/:token" element={<EsignSigningPage />} />

          {/* Opt-out links from marketing messages. They point at this domain (short
              and branded inside an SMS), so the app must handle them — otherwise
              they open a blank page. The pages call the backend and show the result. */}
          <Route path="/s/:code" element={<SmsOptOutPage />} />
          {/* Short links inside a text (CG, 2026-08-28: "reduce the characters
              used by using a link shortener"). Public, no auth - resolves the
              code with the backend and forwards. */}
          <Route path="/l/:code" element={<ShortLinkPage />} />
          {/* The same redirect, minted per recipient, so Analytics can name the
              buyer who tapped "Join the Collings platform" or "Update your
              property preferences" (CG, Monday 13068863308). Public, no auth. */}
          <Route path="/k/:code" element={<ShortLinkPage tracked />} />
          <Route path="/notifications/sms/stop/:code" element={<SmsOptOutPage />} />
          <Route path="/notifications/email/unsubscribe" element={<EmailUnsubscribePage />} />

          {/* Shareable report links emailed to vendors and clients. These MUST be
              frontend routes: crm.collings.com.au serves only this SPA, so a
              backend URL on that host would open a blank page. The share token
              in the path is the credential — no login, no layout. */}
          <Route path="/cma/:token" element={<CmaPublicReportPage />} />
          <Route path="/market-update/:token" element={<MarketUpdatePublicPage />} />

          <Route path="/login" element={<Login />} />
          <Route path="/account-setup" element={<AccountSetup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/account-verification" element={<AccountVerification />} />
          {/* Opened from the emailed "be my Personal Assistant" link, so it
              must work signed out - the token in the URL is the proof. */}
          <Route path="/assistant-invite" element={<AssistantInvite />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* ***** App routes (behind auth + layout) ***** */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardOrRedirect />} />
            {/* AI Voice Caller Routes. Moved under Prospecting in the sidebar and
                closed off until Christian opens it up (Monday 12780827140), so the
                pages are admin-only for now. */}
            <Route path="ai-voice-caller" element={<ComingSoonRoute title="AI Voice Caller"><AiCallCenter /></ComingSoonRoute>} />
            <Route path="ai-calls" element={<ComingSoonRoute title="AI Voice Caller"><AiCallCenter /></ComingSoonRoute>} />
            <Route path="campaign-setup" element={<ComingSoonRoute title="AI Voice Caller"><CampaignSetupWizard /></ComingSoonRoute>} />
            <Route path="voice-agents" element={<ComingSoonRoute title="AI Voice Caller"><VoiceAgent /></ComingSoonRoute>} />
            {/* Campaign Manager */}
            <Route path="campaign-manager" element={<CampaignManager />} />
            {/* eSign (authenticated) */}
            <Route path="esign" element={<EsignHomePage />} />
            <Route path="esign/agreements" element={<EsignAgreementsPage />} />
            <Route path="esign/envelope/:id" element={<EnvelopeEditorPage />} />
            <Route path="esign/authority/:id" element={<AuthorityFormPage />} />
            <Route path="esign/contract-of-sale/:id" element={<ContractOfSaleFormPage />} />
            <Route path="esign/document/:id" element={<EnvelopeDetailPage />} />
            <Route path="properties" element={<PropertyListing />} />
            <Route path="add-property" element={<AddProperty />} />
            <Route path="properties/add-property" element={<AddProperty />} />
            <Route path="properties/edit/:id" element={<AddProperty isEdit={true} />} />
            <Route path="properties/trash" element={<PropertyTrashPage />} />
            <Route path="properties/:id" element={<MailtoComposerScope><PropertyDetailPage /></MailtoComposerScope>} />
            <Route path="/property/:id" element={<MailtoComposerScope><PropertyDetailPage /></MailtoComposerScope>} />
            {/* Collings Properties Routes */}
            <Route path="collings-properties" element={<CollingsPropertiesPage />} />
            <Route path="collings-properties/map" element={<CollingsPropertiesMapPage />} />
            {/* Merged into PropertyTrashPage (single Property Trash covering both
                datasets) - route kept so old links/bookmarks still land somewhere. */}
            <Route path="collings-properties/trash" element={<PropertyTrashPage />} />
            <Route path="collings-properties/add" element={<AddCollingsPropertyPage />} />
            <Route path="collings-properties/edit/:id" element={<AddCollingsPropertyPage />} />
            <Route path="collings-properties/:id" element={<MailtoComposerScope><CollingsPropertyDetailPage /></MailtoComposerScope>} />
            {/* Public Properties - every Database property as a profile. Kept
                above the Public Property Search routes so the two are read
                together: this is the CRM's own lower property layer, that one
                is the external comparable-sales feed. */}
            <Route path="public-properties" element={<DatabasePropertiesPage />} />
            <Route path="public-properties/:recordId" element={<DatabasePropertyProfilePage />} />
            {/* Public Property Search Routes */}
            <Route path="public-property-search" element={<PublicPropertiesPage />} />
            <Route path="public-property-search/add" element={<AddPublicPropertyPage />} />
            <Route path="public-property-search/edit/:id" element={<AddPublicPropertyPage />} />
            <Route path="public-property-search/:id" element={<PublicPropertyDetailPage />} />
            {/* Public Property Search: every Homer sale and rental campaign in
                the patch. Opened up for everyone (Christian, Monday 12573204628,
                12 Oct 2026); it was the closed-off "Search" row. The older
                Collings-Data detail pages stay so existing deep links resolve. */}
            <Route path="collings-data-properties" element={<CollingsDataPropertiesPage />} />
            <Route path="collings-data-properties/property/:id" element={<PublicPropertyProfilePage />} />
            {/* Old per-source links open the property's one profile (Monday 12573204628, 26 Oct 2026). */}
            <Route
                path="collings-data-properties/homer/:id"
                element={<PropertyProfileRedirect kind="homer" fallback={<PublicPropertyListingPage />} />}
            />
            <Route
                path="collings-data-properties/collings/:id"
                element={<PropertyProfileRedirect kind="collings" fallback={<PublicPropertyListingPage source="collings" />} />}
            />
            <Route
                path="collings-data-properties/owner/:key"
                element={<PropertyProfileRedirect kind="owner" fallback={<OwnerRecordPage />} />}
            />
            <Route path="collings-data-properties/preview/:id" element={<CollingsDataPropertyDetailPage />} />
            <Route path="collings-data-properties/:id" element={<CollingsDataPropertyDetailPage />} />
            {/* <Route path="properties/:id" element={<ViewProperty />} /> */}
            {/* <Route path="/property/:id" element={<PropertyDetail />} /> */}
            {/* <Route path="rea-properties" element={<REAProperties />} />
            <Route path="rea-properties/:id" element={<REAPropertyDetail />} /> */}
            <Route path="duplicate-check" element={<DuplicateCheck />} />
            {/* <Route path="enquiry-lead-queues" element={<EnquiryLeadQueues />} /> */}
            <Route path="leads" element={<LeadManagement />} />
            <Route path="seller-leads" element={<SellerLeadsPage />} />
            <Route path="leads-pipeline" element={<PipelinePage />} />
            <Route path="on-market-pipeline" element={<PipelinePageV2 title="On Market Pipeline" defaultType="onmarket" />} />
            <Route path="database" element={<DatabasePage />} />
            {/* Prospects: one nav row for Database, Seller Leads and both
                appraisal boards (Monday 12988442847). Those four pages keep
                their own routes, so links into them keep working. */}
            <Route path="prospects" element={<ProspectsPage />} />
            <Route path="garage-sales" element={<GarageSalesPage />} />

            {/* Reports tab (Monday 13377706735): five buttons into the report
                pages below, which keep their /prospecting/... routes. Open to
                everyone; each button carries its own coming-soon rule. */}
            <Route path="reports" element={<ReportsPage />} />

            {/* Prospecting tab. Search, Garage Sales and Communications live
                under it in the nav but keep their existing routes — the ticket
                asks to move them, not to duplicate them. */}
            <Route path="prospecting" element={<ProspectingLayout />}>
              {/* The section lands on Find Buyers; Market Update and CMA are
                  reached from the Reports tab. */}
              <Route index element={<Navigate to="/prospecting/find-buyers" replace />} />
              <Route path="find-buyers" element={<ProspectingFindBuyersPage />} />
              <Route path="relisting-opportunities" element={<RelistingOpportunitiesPage />} />
              <Route path="new-developments" element={<NewDevelopmentsPage />} />
              {/* Market Update and CMA were admin-only while Christian reviewed
                  them (Monday 12780827140). Opened to agents 29 Oct 2026 so the
                  Reports tab buttons work for them (Monday 13377706735). */}
              <Route path="market-update" element={<MarketUpdatePage />} />
              <Route path="market-update/templates" element={<MarketUpdateTemplatesPage />} />
              <Route path="market-update/:id" element={<MarketUpdateWizardPage />} />
              <Route path="cma" element={<CmaListPage />} />
              <Route path="cma/:id" element={<CmaWizardPage />} />
            </Route>
            <Route path="off-market-pipeline" element={<PipelinePageV2 title="Off Market Pipeline" defaultType="offmarket" />} />
            <Route path="appraisal-submissions" element={<AppraisalSubmissionsPage />} />
            <Route path="contacts" element={<ContactList />} />
            <Route path="contacts/add" element={<ContactFormComponent />} />
            <Route path="contacts/trash" element={<ContactTrashPage />} />
            <Route path="contacts/portal-invites" element={<PortalInvitesPage />} />
            <Route path="contacts/portal-invites/:id" element={<PortalInvitesPage />} />
            <Route path="send-runs" element={<SendRunPage />} />
            <Route path="send-runs/:id" element={<SendRunPage />} />
            <Route path="contacts/:id" element={<MailtoComposerScope><ContactProfilePage /></MailtoComposerScope>} />
            <Route path="contacts/edit/:id" element={<ContactFormComponent isEdit={true} />} />
            <Route path="agents" element={<AgentManagement />} />
            <Route path="buyers" element={<BuyerManagement />} />
            <Route path="kpi" element={<KPIsPage />} />
            <Route path="commissions" element={<CommissionTracker />} />
            <Route path="leaderboard/:metric" element={<LeaderboardStatPage />} />
            <Route path="inspections" element={<InspectionsPage />} />
            <Route path="open-home/:sessionId" element={<OpenHomeSessionPage />} />
            <Route path="tasks" element={<TasksManagement />} />
            <Route path="analytics" element={<AnalyticsDashboardWrapper />} />
            <Route path="analytics/property/:propertyId" element={<PropertyAnalyticsDetailPage />} />
            <Route path="analytics/agent" element={<SuperAdminRoute title="Agent performance" allowTeam><AgentDashboardPage /></SuperAdminRoute>} />
            <Route path="analytics/agent/:agentId" element={<SuperAdminRoute title="Agent dashboard" allowTeam><AgentDashboardPage /></SuperAdminRoute>} />
            {/* My Performance — an agent's own full breakdown (?mine=1), open to any signed-in agent. */}
            <Route path="my-performance" element={<AgentDashboardPage mine />} />
            {/* Investors now live inside Analytics → Investor Analytics; redirect old links. */}
            <Route path="investors" element={<Navigate to="/analytics" replace />} />
            <Route path="investors/:id" element={<Navigate to="/analytics" replace />} />
            {/* <Route path="offers" element={<OffersPage />} />
            <Route path="offers/:id" element={<OfferDetailPage />} /> */}
            <Route path="support-tickets" element={<SuperAdminRoute title="Support Tickets"><SupportTicketsPage /></SuperAdminRoute>} />
            <Route path="property-id-review" element={<SuperAdminRoute title="Property ID Review" message="Property ID review is handled by the data team and super admins."><PropertyIdReviewPage /></SuperAdminRoute>} />
            <Route path="ai-seo" element={<SuperAdminRoute title="AI-SEO"><AiSeoPage /></SuperAdminRoute>} />
            <Route path="report-issue" element={<ReportIssuePage />} />
            <Route path="chat-history" element={<ChatHistory />} />
            <Route path="messenger" element={<MessengerPage />} />
            {/* DOMA Communications Hub - email/SMS campaigns */}
            <Route path="communications" element={<CommunicationsPage />} />
            <Route path="communications/campaigns/:id" element={<CampaignEditorPage />} />
            {/* <Route path="search" element={<Search />} /> */}
            <Route path="settings" element={<Settings />} />
            {/* Access & Permissions is a tab inside Agent Management now
                (Monday 13058000755). The old route still resolves so existing
                links and bookmarks land on the merged tab. */}
            <Route path="access-permissions" element={<Navigate to="/agents?tab=access" replace />} />
            <Route path="my-profile" element={<MyProfile />} />
            <Route path="mailbox" element={<MailboxPage />} />
            {/* <Route path="notification-settings" element={<NotificationSettings />} /> */}
            <Route path="notifications" element={<Notifications />} />
            <Route path="twilio-accounts" element={<TwilioAccountsManagement />} />
            {/* Utilities Routes */}
            <Route path="utilities" element={<CannedResponsePage />} />
            <Route path="email-templates" element={<CannedResponsePage />} />
            <Route path="auto-email-responses" element={<AutoEmailResponses />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="calendar" element={<Calendar />} />
            {/* <Route path="admin-properties" element={<AdminProperties />} /> */}
            <Route path="session" element={<Session />} />
            <Route path="session/:id" element={<SessionDetails />} />
            <Route path="session-analytics" element={<SessionAnalytics />} />

            {/* ✅ Keep the main Google Calendar page inside the app */}
            <Route path="google-calender" element={<GoogleCalender />} />
            {/* <Route path="agent-enquiry-leads" element={<AgentEnquiryLeads />} /> */}

            {/* <Route
              path="/admin-properties/queue-logs/:queueId"
              element={<CallLogsForQueue />}
            /> */}
          </Route>
        </Routes>

        {/* Floating bot + toaster */}
        {/* Remove the chatbot until the LLM is perfected */}
        {/* <ChatBot /> */}
        <ThemedToaster />
        </ThemeProvider>
      </Router>
    </NotificationProvider>
  );
}

export default App;
